import { useState } from 'react'
import { useTranslation } from 'react-i18next'

import { ApiError } from '@/api/problemDetails'
import { Alert, Button, Dialog, toast } from '@/components/ui'
import { useTenants } from '@/features/tenants/queries'

import { useUpdateAgent } from '../queries'
import type { AgentSummary } from '../types'
import { AgentPersonaFields } from './AgentPersonaFields'

interface EditAgentDialogProps {
  readonly tenantId: string
  readonly agent: AgentSummary
  readonly onClose: () => void
}

/**
 * Edita a persona do agente num diálogo acessível. Montado sob demanda (só quando aberto), então os
 * campos já iniciam com os valores do agente via inicializadores de estado — sem reset em efeito.
 */
export function EditAgentDialog({ tenantId, agent, onClose }: EditAgentDialogProps) {
  const { t } = useTranslation()
  const [name, setName] = useState(agent.name)
  const [description, setDescription] = useState(agent.description ?? '')
  const [systemPrompt, setSystemPrompt] = useState(agent.systemPrompt ?? '')
  const mutation = useUpdateAgent(tenantId)
  // Nome da organização para os modelos de instruções (lista já em cache; ausente = texto genérico).
  const companyName = useTenants().data?.find((tenant) => tenant.id === tenantId)?.name

  const save = () => {
    if (name.trim().length === 0 || mutation.isPending) {
      return
    }
    mutation.mutate(
      {
        agentId: agent.id,
        persona: {
          name: name.trim(),
          description: description.trim(),
          systemPrompt: systemPrompt.trim(),
        },
      },
      {
        onSuccess: () => {
          onClose()
          toast.success(t('agents.toast.updated'))
        },
        onError: (error) => {
          toast.error(error instanceof ApiError ? error.problem.title : t('agents.actionFailed'))
        },
      },
    )
  }

  return (
    <Dialog
      open
      onClose={onClose}
      title={t('agents.edit.title')}
      description={t('agents.edit.subtitle')}
      footer={
        <>
          <Button variant="ghost" disabled={mutation.isPending} onClick={onClose}>
            {t('agents.edit.cancel')}
          </Button>
          <Button disabled={name.trim().length === 0 || mutation.isPending} onClick={save}>
            {mutation.isPending ? t('agents.edit.saving') : t('agents.edit.save')}
          </Button>
        </>
      }
    >
      <div className="flex flex-col gap-3">
        {mutation.isError ? (
          <Alert variant="danger" title={t('agents.actionFailed')}>
            {mutation.error instanceof ApiError ? mutation.error.problem.title : null}
          </Alert>
        ) : null}
        <AgentPersonaFields
          name={name}
          description={description}
          systemPrompt={systemPrompt}
          companyName={companyName}
          disabled={mutation.isPending}
          onName={setName}
          onDescription={setDescription}
          onSystemPrompt={setSystemPrompt}
        />
      </div>
    </Dialog>
  )
}
